import { TextStyle } from 'react-native'
import { createUseStyles } from '../../styles/create-use-styles'
import { useGenerateTokens, TextTokens } from './useGenerateTokens'

import type { TextDescriptions, TextProps } from './text.props'
import { TextDescriptionVariant } from './constants'

interface TextStyles {
  text: TextStyle
}

/**
 * 🚨🚨🚨 DO NOT ADD ANY NEW TOKENS WITHOUT CONSULTING THE DESIGN TEAM 🚨🚨🚨
 */

export const getTokenForDescription = (tokens: TextTokens, textDescription: TextDescriptions) => {
  switch (textDescription) {
    // Primary Fonts (Archivo)
    case TextDescriptionVariant.TEXT_2XS_REGULAR:
      return tokens.Text2XSRegular
    case TextDescriptionVariant.TEXT_2XS_SEMIBOLD:
      return tokens.Text2XSSemiBold
    case TextDescriptionVariant.TEXT_2XS_SEMIBOLD_ITALIC:
      return tokens.Text2XSSemiBoldItalic
    case TextDescriptionVariant.TEXT_2XS_XBOLD:
      return tokens.Text2XSXBold
    case TextDescriptionVariant.TEXT_2XS_XBOLD_ITALIC:
      return tokens.Text2XSXBoldItalic

    case TextDescriptionVariant.TEXT_XS_REGULAR:
      return tokens.TextXSRegular
    case TextDescriptionVariant.TEXT_XS_REGULAR_ITALIC:
      return tokens.TextXSRegularItalic
    case TextDescriptionVariant.TEXT_XS_SEMIBOLD:
      return tokens.TextXSSemiBold
    case TextDescriptionVariant.TEXT_XS_SEMIBOLD_ITALIC:
      return tokens.TextXSSemiBoldItalic
    case TextDescriptionVariant.TEXT_XS_BOLD:
      return tokens.TextXSBold
    case TextDescriptionVariant.TEXT_XS_BOLD_ITALIC:
      return tokens.TextXSBoldItalic

    case TextDescriptionVariant.TEXT_SMALL_REGULAR:
      return tokens.TextSmallRegular
    case TextDescriptionVariant.TEXT_SMALL_SEMIBOLD:
      return tokens.TextSmallSemiBold
    case TextDescriptionVariant.TEXT_SMALL_BOLD:
      return tokens.TextSmallBold
    case TextDescriptionVariant.TEXT_SMALL_XBOLD:
      return tokens.TextSmallXBold

    case TextDescriptionVariant.TEXT_BASE_REGULAR:
      return tokens.TextBaseRegular
    case TextDescriptionVariant.TEXT_BASE_SEMIBOLD:
      return tokens.TextBaseSemiBold
    case TextDescriptionVariant.TEXT_BASE_BOLD:
      return tokens.TextBaseBold
    case TextDescriptionVariant.TEXT_BASE_BOLD_ITALIC:
      return tokens.TextBaseBoldItalic
    case TextDescriptionVariant.TEXT_BASE_XBOLD:
      return tokens.TextBaseXBold
    case TextDescriptionVariant.TEXT_BASE_BLACK:
      return tokens.TextBaseBlack

    case TextDescriptionVariant.TEXT_BODY_REGULAR:
      return tokens.TextBodyRegular
    case TextDescriptionVariant.TEXT_BODY_REGULAR_ITALIC:
      return tokens.TextBodyRegularItalic
    case TextDescriptionVariant.TEXT_BODY_BOLD:
      return tokens.TextBodyBold
    case TextDescriptionVariant.TEXT_BODY_BOLD_ITALIC:
      return tokens.TextBodyBoldItalic

    case TextDescriptionVariant.TEXT_MD_REGULAR:
      return tokens.TextMdRegular
    case TextDescriptionVariant.TEXT_MD_SEMIBOLD:
      return tokens.TextMdSemiBold
    case TextDescriptionVariant.TEXT_MD_XBOLD:
      return tokens.TextMdXBold
    case TextDescriptionVariant.TEXT_MD_BLACK:
      return tokens.TextMdBlack

    case TextDescriptionVariant.TEXT_LG_REGULAR:
      return tokens.TextLgRegular
    case TextDescriptionVariant.TEXT_LG_BOLD:
      return tokens.TextLgBold
    case TextDescriptionVariant.TEXT_LG_BOLD_ITALIC:
      return tokens.TextLgBoldItalic
    case TextDescriptionVariant.TEXT_LG_BLACK:
      return tokens.TextLgBlack

    case TextDescriptionVariant.TEXT_XL_XBOLD:
      return tokens.TextXLXBold
    case TextDescriptionVariant.TEXT_XL_BLACK:
      return tokens.TextXLBlack

    case TextDescriptionVariant.TEXT_2XL_REGULAR:
      return tokens.Text2XLRegular
    case TextDescriptionVariant.TEXT_2XL_BOLD:
      return tokens.Text2XLBold
    case TextDescriptionVariant.TEXT_2XL_BLACK:
      return tokens.Text2XLBlack

    case TextDescriptionVariant.TEXT_3XL_XBOLD:
      return tokens.Text3XLXBold
    case TextDescriptionVariant.TEXT_3XL_BLACK:
      return tokens.Text3XLBlack

    case TextDescriptionVariant.TEXT_4XL_REGULAR:
      return tokens.Text4XLRegular
    case TextDescriptionVariant.TEXT_4XL_BLACK:
      return tokens.Text4XLBlack

    case TextDescriptionVariant.TEXT_5XL_XBOLD:
      return tokens.Text5XLXBold
    case TextDescriptionVariant.TEXT_5XL_BLACK:
      return tokens.Text5XLBlack

    // Secondary Fonts (Archivo Narrow)
    case TextDescriptionVariant.TEXT_2XS_NARROW_REGULAR:
      return tokens.Text2XSNarrowRegular
    case TextDescriptionVariant.TEXT_2XS_NARROW_BOLD:
      return tokens.Text2XSNarrowBold

    case TextDescriptionVariant.TEXT_XS_NARROW_REGULAR:
      return tokens.TextXSNarrowRegular
    case TextDescriptionVariant.TEXT_XS_NARROW_SEMIBOLD:
      return tokens.TextXSNarrowSemiBold
    case TextDescriptionVariant.TEXT_XS_NARROW_BOLD:
      return tokens.TextXSNarrowBold

    case TextDescriptionVariant.TEXT_SMALL_NARROW_REGULAR:
      return tokens.TextSmallNarrowRegular
    case TextDescriptionVariant.TEXT_SMALL_NARROW_BOLD:
      return tokens.TextSmallNarrowBold

    case TextDescriptionVariant.TEXT_BASE_NARROW_REGULAR:
      return tokens.TextBaseNarrowRegular
    case TextDescriptionVariant.TEXT_BASE_NARROW_BOLD:
      return tokens.TextBaseNarrowBold

    default:
      // Make it obvious you've not passed the correct info in
      return tokens.Text5XLBlack
  }
}

export const useStyles = createUseStyles<TextStyles, TextProps>((theme, props): TextStyles => {
  const {
    colorOverride = null,
    textAlign,
    variant,
    uppercase,
    underline,
    capitalize,
    shrink,
    textWrap,
    lineHeightOverride,
  } = props
  const tokens = useGenerateTokens(theme, theme.colors.textColor)

  const textStyle = getTokenForDescription(tokens, variant)
  if (textAlign) {
    textStyle.textAlign = textAlign
  }

  if (uppercase) {
    textStyle.textTransform = 'uppercase'
  }

  if (underline) {
    textStyle.textDecorationLine = 'underline'
  }

  if (capitalize) {
    textStyle.textTransform = 'capitalize'
  }

  if (colorOverride) {
    textStyle.color = colorOverride
  }

  if (shrink) {
    textStyle.flexShrink = 1
  }

  if (lineHeightOverride) {
    textStyle.lineHeight = theme.calcUnit(lineHeightOverride)
  }

  if (textWrap) {
    // @ts-expect-error
    textStyle.textWrap = textWrap
    // @ts-expect-error
    textStyle.whiteSpace = textWrap.toLowerCase()

    if (textWrap === 'wrap') {
      // @ts-expect-error
      textStyle.overflowWrap = 'anywhere'
    }
  }

  return {
    text: textStyle,
  }
})
